import React, { useState } from "react";

import Drawer from "./Drawer";
import { type SignalAdminDraft, type SignalAdminRegistry } from "./types";

interface DataLayersPanelProps {
  registry: SignalAdminRegistry;
  draft: SignalAdminDraft;
  onDraftChange: (next: SignalAdminDraft) => void;
  onOpenSignal: (id: string) => void;
}

const DataLayersPanel: React.FC<DataLayersPanelProps> = ({
  registry,
  draft,
  onDraftChange,
  onOpenSignal,
}) => {
  const [inspecting, setInspecting] = useState<string | null>(null);
  const titleOf = (id: string) =>
    registry.definitions.find(d => d.id === id)?.title ?? id;
  const layer = registry.layers.find(l => l.id === inspecting);

  const setLayer = (id: string, enabled: boolean) =>
    onDraftChange({ ...draft, layers: { ...draft.layers, [id]: enabled } });

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h3 className="text-[15px] font-bold text-gray-900">Data Layers</h3>
        <p className="mt-0.5 max-w-3xl text-[12.5px] text-gray-500">
          Turning a layer off blocks every signal that needs it for all
          organizations: those rules stop being evaluated and their open signals
          are hidden, not resolved. The data itself keeps flowing in — this
          doesn&apos;t touch ingestion.
        </p>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <ul className="divide-y divide-gray-100">
          {registry.layers.map(l => {
            const enabled = draft.layers[l.id] !== false;
            const staged = enabled !== l.enabled;
            return (
              <li
                key={l.id}
                className="flex flex-wrap items-center gap-x-6 gap-y-2 px-5 py-3.5"
              >
                <div className="min-w-[220px] flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] font-semibold text-gray-900">
                      {l.label}
                    </span>
                    {staged && (
                      <span className="text-[10.5px] font-semibold text-amber-700">
                        Unapplied
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 text-[11.5px] text-gray-500">
                    {l.source} · <span className="font-mono">{l.id}</span> ·{" "}
                    {l.legacy}
                  </div>
                </div>
                <div className="w-[130px] text-[11.5px] text-gray-500">
                  <span className="text-[15px] font-bold text-gray-900 tabular-nums">
                    {l.dependent_implemented}
                  </span>{" "}
                  live rule{l.dependent_implemented === 1 ? "" : "s"}
                  <div className="text-[10.5px] text-gray-400">
                    {l.dependent_definitions.length} in the registry
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setInspecting(l.id)}
                  className="rounded-md border border-gray-200 px-2.5 py-1 text-[12px] font-semibold text-gray-600 hover:border-gray-300 hover:text-gray-900"
                >
                  Inspect
                </button>
                <label className="flex items-center gap-2">
                  <span
                    className={`w-[58px] text-right text-[11.5px] font-bold ${
                      enabled ? "text-emerald-700" : "text-red-700"
                    }`}
                  >
                    {enabled ? "Enabled" : "Disabled"}
                  </span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={enabled}
                    aria-label={`${l.label} layer`}
                    onClick={() => setLayer(l.id, !enabled)}
                    className={`relative h-5 w-9 shrink-0 rounded-full transition-colors ${
                      enabled ? "bg-emerald-600" : "bg-gray-300"
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${
                        enabled ? "left-[18px]" : "left-0.5"
                      }`}
                    />
                  </button>
                </label>
              </li>
            );
          })}
        </ul>
      </div>

      {layer && (
        <Drawer
          open
          onClose={() => setInspecting(null)}
          eyebrow={
            <span className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-brand-dark">
              Data layer
            </span>
          }
          title={layer.label}
          subtitle={layer.id}
        >
          <dl className="m-0 mb-6 text-[12.5px]">
            {[
              ["Source", layer.source],
              ["Former label", layer.legacy],
              ["Expected cadence", layer.expected_cadence],
              ["Record-age policy", layer.record_age_policy],
            ].map(([label, value]) => (
              <div
                key={label}
                className="grid grid-cols-[140px_minmax(0,1fr)] gap-3 py-1.5"
              >
                <dt className="text-gray-500">{label}</dt>
                <dd className="m-0 text-gray-900">{value}</dd>
              </div>
            ))}
          </dl>
          <h3 className="mb-2.5 text-[10.5px] font-bold uppercase tracking-[0.08em] text-gray-400">
            Signals that need this layer
          </h3>
          <div className="flex flex-wrap gap-1.5">
            {layer.dependent_definitions.map(id => (
              <button
                key={id}
                type="button"
                onClick={() => {
                  setInspecting(null);
                  onOpenSignal(id);
                }}
                className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[11.5px] text-gray-700 hover:border-brand hover:text-brand"
              >
                {titleOf(id)}
              </button>
            ))}
          </div>
        </Drawer>
      )}
    </div>
  );
};

export default DataLayersPanel;
